import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../config/rbac';
import { PAGE_SIZE, formatDate } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { toastSuccess } from '../lib/toast';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { FilterSubmitButton } from '../components/FilterSubmitButton';
import { ConfirmDialog } from '../components/Modal';
import { CouponQrModal } from '../components/CouponQrModal';
import { IconPencil, IconTrash, IconQr } from '../components/Icons';

export default function CouponList() {
  const { hasRole } = useAuth();
  const isSuper = hasRole(ROLES.SUPER_ADMIN);
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [scannedFilter, setScannedFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState(() => searchParams.get('brandId') || '');
  const [brands, setBrands] = useState([]);
  const [quotaRemaining, setQuotaRemaining] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [qrCoupon, setQrCoupon] = useState(null);

  const brandIdFromUrl = searchParams.get('brandId') || '';
  const filteredBrandName = brands.find((b) => b.id === brandIdFromUrl)?.name;

  useEffect(() => {
    if (!isSuper) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.listBrands({ limit: 200 });
        if (!cancelled) setBrands(data?.items || []);
      } catch {
        /* filter dropdown is optional */
      }
    })();
    return () => { cancelled = true; };
  }, [isSuper]);

  useEffect(() => {
    setBrandFilter(brandIdFromUrl);
    setSkip(0);
  }, [brandIdFromUrl]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { skip, limit, search: search.trim() || undefined };
      if (scannedFilter === 'yes') params.scanned = true;
      if (scannedFilter === 'no') params.scanned = false;
      if (isSuper && brandIdFromUrl) params.brandId = brandIdFromUrl;
      const raw = await api.listCoupons(params);
      const norm = normalizeListResponse(raw, limit);
      setRows(norm.items);
      setTotalCount(norm.totalCount);
      if (raw?.quotaRemaining !== undefined) setQuotaRemaining(raw.quotaRemaining);
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [skip, limit, search, scannedFilter, isSuper, brandIdFromUrl]);

  useEffect(() => { load(); }, [load]);

  function applyFilters() {
    setSkip(0);
    if (!isSuper) return;
    const next = new URLSearchParams(searchParams);
    if (brandFilter) next.set('brandId', brandFilter);
    else next.delete('brandId');
    setSearchParams(next, { replace: true });
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    try {
      await api.deleteCoupon(pendingDelete);
      toastSuccess('Coupon deleted.');
      setPendingDelete(null);
      await load();
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setDeleteBusy(false);
    }
  }

  const columns = [
    { key: 'code', label: 'Code' },
    ...(isSuper ? [{ key: 'brand', label: 'Brand', render: (r) => r.brandName || '—' }] : []),
    {
      key: 'scanned',
      label: 'Status',
      render: (r) => (
        <span className={`badge ${r.scanned ? 'text-bg-success' : 'text-bg-warning'}`}>
          {r.scanned ? 'Scanned' : 'Pending'}
        </span>
      ),
    },
    { key: 'scanCount', label: 'Scans', render: (r) => r.scanCount ?? 0 },
    { key: 'created', label: 'Created', render: (r) => formatDate(r.createdAt) },
    {
      key: 'qr',
      label: 'QR',
      width: '110px',
      render: (r) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary coupon-list-qr-btn"
          onClick={() => setQrCoupon({ code: r.code, brandName: r.brandName })}
        >
          <IconQr size={15} />
          View QR
        </button>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '120px',
      render: (r) => (
        <div className="table-actions">
          <Link to={`/coupons/${r.couponId}`} className="btn-table-icon btn-table-icon--edit" aria-label="Edit" title="Edit metadata">
            <IconPencil size={16} />
          </Link>
          <button type="button" className="btn-table-icon btn-table-icon--delete" aria-label="Delete" title="Delete" onClick={() => setPendingDelete(r.couponId)}>
            <IconTrash size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Coupons"
        subtitle={
          filteredBrandName
            ? `Showing coupons for ${filteredBrandName}`
            : quotaRemaining != null
              ? `Coupon quota remaining: ${quotaRemaining}`
              : 'Manage authenticity coupon codes'
        }
        actions={<Link to="/coupons/generate" className="btn btn-primary">Generate coupons</Link>}
      />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label small mb-1">Search code</label>
              <input className="form-control" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code contains…" />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">Scan status</label>
              <select className="form-select" value={scannedFilter} onChange={(e) => setScannedFilter(e.target.value)}>
                <option value="">All</option>
                <option value="no">Pending</option>
                <option value="yes">Scanned</option>
              </select>
            </div>
            {isSuper ? (
              <div className="col-md-3">
                <label className="form-label small mb-1">Brand</label>
                <select className="form-select" value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
                  <option value="">All brands</option>
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="col-md-2">
              <FilterSubmitButton onClick={applyFilters} disabled={loading} />
            </div>
          </div>
        </CardBody>
      </Card>
      <Card>
        {loading ? (
          <CardBody className="text-center py-5"><div className="spinner-border text-primary" role="status" /></CardBody>
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={rows}
              emptyTitle="No coupons"
              emptyDescription={
                brandIdFromUrl
                  ? filteredBrandName
                    ? `No coupon codes found for ${filteredBrandName}.`
                    : 'No coupon codes found for this brand.'
                  : 'Generate coupon codes to start verifying goods.'
              }
              rowKey={(r) => r.couponId}
            />
            <Pagination totalCount={totalCount} skip={skip} limit={limit} onChange={({ skip: s }) => setSkip(s)} />
          </>
        )}
      </Card>
      {qrCoupon ? <CouponQrModal coupon={qrCoupon} onClose={() => setQrCoupon(null)} /> : null}
      {pendingDelete ? (
        <ConfirmDialog title="Delete coupon?" message="This removes the coupon from active use." confirmLabel="Delete" danger busy={deleteBusy} onConfirm={confirmDelete} onCancel={() => setPendingDelete(null)} />
      ) : null}
    </>
  );
}
